import { parse, format } from 'date-fns';
import { AnalyticsInstance } from './analytics';
import { SERVER_FORMAT_DATE, IMAGES_ROOT } from './constants';
import * as cookies from './cs';

const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss XXX';

interface INode {
    id: string;
}

interface IItem {
    name: string;
    description: string;
    price: number;
    quantity: number;
    imageUrl: string;
    url: string;
    dataFields: {
        product_id: number;
        arrival_date: string;
        departure_date: string;
        name: string;
        variant: string;
        brand: string;
        price_strike_out: number | null;
        category: { id: number; name: string; slug: string };
        destination: { id: number; name: string; slug: string };
    };
}

interface IPurchaseItem extends INode, IItem {}

export const convCartItemsToPurchaseItems = (
    cartItems: Array<{
        arrival_date: string;
        trip_name: string;
        activity: { id: string; slug: string };
        partner_name: string;
        product_category_info: { id: string; name: string; slug: string };
        product_city_info: { id: string; name: string; slug: string };
        tickets: {
            [key: string]: {
                id: string;
                name: string;
                description: string;
                price: string;
                strike_out: string;
                booked: string;
            };
        };
    }>
) => {
    try {
        const items: IPurchaseItem[] = [];
        cartItems.forEach((item) => {
            Object.values(item.tickets).forEach((ticket) => {
                const date = parse(item.arrival_date, SERVER_FORMAT_DATE, new Date()).getTime();
                items.push({
                    id: parseInt(ticket.id, 10).toString(),
                    name: [item.trip_name, ticket.name].filter(Boolean).join(' - '),
                    description: ticket.description,
                    price: parseFloat(ticket.price),
                    quantity: parseInt(ticket.booked, 10),
                    imageUrl: `${IMAGES_ROOT}activity/${item.activity.id}/960x720/${item.activity.slug}.jpg`,
                    url: `https://www.tripshock.com/${item.activity.slug}/details/${item.activity.id}/`,
                    dataFields: {
                        product_id: parseInt(item.activity.id, 10),
                        arrival_date: format(date, DATE_FORMAT),
                        departure_date: format(date + 86400 * 7 * 1000, DATE_FORMAT),
                        name: item.trip_name,
                        variant: ticket.name,
                        brand: item.partner_name,
                        price_strike_out: ticket.strike_out ? parseFloat(ticket.strike_out) : null,
                        destination: {
                            ...item.product_city_info,
                            id: parseInt(item.product_city_info.id, 10),
                        },
                        category: {
                            ...item.product_category_info,
                            id: parseInt(item.product_category_info.id, 10),
                        },
                    },
                });
            });
        });

        return items;
    } catch (err) {
        if (process.env.NODE_ENV === 'development') {
            console.error(err);
        }
        return [];
    }
};

export const trackUpdateCart = async (
    items: IPurchaseItem[]
): Promise<{
    statusCode: number;
    data: any;
}> => {
    return new Promise((res) =>
        window._iaq.push([
            'updateCart',
            items,
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackAttractions = async (payload: {
    category: { id: number; slug: string; name: string } | null;
    destination: { id: number; slug: string; name: string };
    arrivalDate: number;
    departureDate: number;
}): Promise<{
    statusCode: number;
    data: any;
}> => {
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'attractions',
            Object.assign({}, payload, {
                arrival_date: format(payload.arrivalDate, DATE_FORMAT),
                departure_date: format(payload.departureDate, DATE_FORMAT),
            }),
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackProductDetail = async (payload: {
    product_id: number;
    brand: string;
    category: { id: number; slug: string; name: string };
    destination: { id: number; slug: string; name: string };
    image: string;
    name: string;
    price: number;
    price_strike_out: number | null;
}): Promise<{
    statusCode: number;
    data: any;
}> => {
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'productDetail',
            Object.assign({}, payload, { path: AnalyticsInstance.wpage }),
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackWishListAdded = async (payload: {
    id: number | string;
    slug: string;
}): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'wishListAdded',
            {
                product_id: typeof payload.id === 'string' ? parseInt(payload.id, 10) : payload.id,
                originalPath: AnalyticsInstance.wpage,
                path: `/${payload.slug}/details/${payload.id}/`,
            },
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackWishListRemoved = async (payload: {
    id: number | string;
    slug: string;
}): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'wishListRemoved',
            {
                product_id: typeof payload.id === 'string' ? parseInt(payload.id, 10) : payload.id,
                originalPath: AnalyticsInstance.wpage,
                path: `/${payload.slug}/details/${payload.id}/`,
            },
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackPurchase = async (
    grossTotal: number,
    promoDetails: { name: string; code: string },
    items: Array<IPurchaseItem>
): Promise<{ statusCode: number; data: any }> => {
    if (!window._iaq.user || !window._iaq.user.email) {
        return Promise.resolve({
            statusCode: 400,
            data: { msg: 'Invalid email address' },
        });
    }

    const campaignId = parseInt(cookies.get('iterableEmailCampaignId'), 10) || 0;
    const templateId = parseInt(cookies.get('iterableTemplateId'), 10) || 0;

    const dataReq: any = {
        user: {
            email: window._iaq.user?.email,
        },
        total: grossTotal,
        items,
        promoCode: promoDetails.code.toLowerCase(),
        promoName: promoDetails.name,
    };
    if (campaignId) {
        dataReq.campaignId = campaignId;
    }
    if (templateId) {
        dataReq.templateId = templateId;
    }

    return new Promise((res) =>
        window._iaq.post('/api/commerce/trackPurchase', dataReq, (statusCode: number, data: any) =>
            res({ statusCode, data })
        )
    );
};

export const trackCheckout = async (
    stage: 1 | 2,
    products: Array<IItem>
): Promise<{ statusCode: number; data: any }> => {
    if (!window._iaq.user || !window._iaq.user.email) {
        return Promise.resolve({
            statusCode: 400,
            data: { msg: 'Invalid email address' },
        });
    }

    return new Promise((res) =>
        window._iaq.post(
            '/api/events/track',
            {
                email: window._iaq.user?.email,
                eventName: 'checkout',
                dataFields: { stage, products },
            },
            (statusCode: number, data: any) => res({ statusCode, data })
        )
    );
};

export const trackPageView = async (payload: {
    path?: string;
    title?: string;
}): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'pageView',
            {
                path: payload.path || AnalyticsInstance.wpage,
                title: payload.title || document.title,
            },
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackPromoCodeRequest = async (payload: {
    success: boolean;
    name: string;
    code: string;
}): Promise<{ statusCode: number; data: any }> => {
    AnalyticsInstance.SendToGAInfoAboutPromoCode(payload.success, payload.code);
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'promoCodeRequest',
            Object.assign({}, payload, {
                code: payload.code.toLowerCase(),
                path: AnalyticsInstance.wpage,
            }),
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const trackSubscribed = (payload: {
    email: string;
}): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.push([
            'track',
            'subscribed',
            Object.assign({}, payload, {
                path: AnalyticsInstance.wpage,
            }),
            (statusCode: number, data: any) => res({ statusCode, data }),
        ])
    );
};

export const userUpdate = async (
    email: string,
    payload: {
        firstName: string;
        lastName: string;
        fullName: string;
        picture: string;
        phoneNumber: string;
    }
): Promise<{ statusCode: number; data: any }> => {
    // pre-save to be able to work with asynchronous events
    const dataFields = {};
    if (payload.firstName && payload.firstName !== window._iaq?.user?.data?.firstName) {
        dataFields.firstName = payload.firstName;
    }
    if (payload.lastName && payload.lastName !== window._iaq?.user?.data?.lastName) {
        dataFields.lastName = payload.lastName;
    }
    if (
        payload.fullName &&
        (!window._iaq?.user?.data?.fullName || !window._iaq.user.data.fullName.includes(' ')) &&
        payload.fullName !== window._iaq?.user?.data?.fullName
    ) {
        dataFields.fullName = payload.fullName;
    }
    if (payload.picture && payload.picture !== window._iaq?.user?.data?.picture) {
        dataFields.picture = payload.picture;
    }
    if (payload.phoneNumber && payload.phoneNumber !== window._iaq?.user?.data?.phoneNumber) {
        dataFields.phoneNumber = payload.phoneNumber;
    }

    window._iaq.user = {
        email,
        data: { ...window._iaq?.user?.data, ...dataFields },
    };

    return new Promise((res) =>
        window._iaq.post('/api/users/update', { email, dataFields }, (statusCode, data) => {
            if (statusCode !== 200) {
                // reset if the user is not saved, possibly an incorrect email
                window._iaq.user = null;
            }
            res({ statusCode, data });
        })
    );
};

export const userLogIn = async (
    email: string,
    dataFields: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
    }
): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.post(
            '/api/events/track',
            {
                email: email,
                eventName: 'login',
                dataFields,
            },
            (statusCode: number, data: any) => res({ statusCode, data })
        )
    );
};

export const userLogOut = async (
    email: string,
    dataFields: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
    }
): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.post(
            '/api/events/track',
            {
                email: email,
                eventName: 'logout',
                dataFields,
            },
            (statusCode: number, data: any) => res({ statusCode, data })
        )
    );
};

export const userResetPassword = async (
    email: string,
    dataFields: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
    }
): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.post(
            '/api/events/track',
            {
                email: email,
                eventName: 'password reset',
                dataFields,
            },
            (statusCode: number, data: any) => res({ statusCode, data })
        )
    );
};

export const userComfirmEmail = async (
    email: string,
    dataFields: {
        firstName: string;
        lastName: string;
        phoneNumber: string;
    }
): Promise<{ statusCode: number; data: any }> => {
    return new Promise((res) =>
        window._iaq.post(
            '/api/events/track',
            {
                email: email,
                eventName: 'email confirmed',
                dataFields,
            },
            (statusCode: number, data: any) => res({ statusCode, data })
        )
    );
};
